import type { Tables } from "./supabase";

// ── Signal Desk ─────────────────────────────────────────────────
export type PostKind = "Article" | "Video" | "Gallery";

export type SignalPostRow = Tables<"signal_posts">;

export interface SignalPost {
  id: string;
  slug: string;
  kind: PostKind;
  title: string;
  excerpt: string;
  body: string | null;
  date: string;
  /**
   * Last edit timestamp from `signal_posts.updated_at`. Used by SEO meta
   * (`article:modified_time`) so client-side render matches worker SSR.
   */
  updatedAt: string | null;
  author: string;
  sourceName: string;
  sourceUrl: string;
  sourceCta: string | null;
  tags: string[];
  image: string | null;
  youtubeId: string | null;
  gallery: string[] | null;
  pinned: boolean;
  isPublished: boolean;
  editorPick: boolean;
  voteScore: number;
  upvoteCount: number;
  downvoteCount: number;
  importance: number;
  durationSeconds: number | null;
}

/** Map a Supabase row to the camelCase SignalPost interface */
export const toSignalPost = (row: SignalPostRow): SignalPost => ({
  id: row.id,
  slug: row.slug,
  kind: row.kind as PostKind,
  title: row.title,
  excerpt: row.excerpt,
  body: row.body,
  date: row.date,
  updatedAt: row.updated_at ?? null,
  author: row.author,
  sourceName: row.source_name,
  sourceUrl: row.source_url,
  sourceCta: row.source_cta,
  tags: row.tags,
  image: row.image,
  youtubeId: row.youtube_id,
  gallery: row.gallery,
  pinned: row.pinned,
  isPublished: row.is_published,
  editorPick: row.editor_pick ?? false,
  voteScore: row.vote_score ?? 0,
  upvoteCount: row.upvote_count ?? 0,
  downvoteCount: row.downvote_count ?? 0,
  importance: row.importance ?? 0,
  durationSeconds: row.duration_seconds ?? null,
});

// ── Blog ────────────────────────────────────────────────────────
export type BlogPostRow = Tables<"blog_posts">;

export type BlogStatus = "draft" | "published" | "scheduled";

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  author: string;
  date: string;
  tags: string[];
  image: string | null;
  readingTimeMin: number | null;
  status: BlogStatus;
  scheduledAt: string | null;
}

/** Map a Supabase row to the camelCase BlogPost interface */
export const toBlogPost = (row: BlogPostRow): BlogPost => ({
  id: row.id,
  slug: row.slug,
  title: row.title,
  excerpt: row.excerpt,
  body: row.body,
  author: row.author,
  date: row.date,
  tags: row.tags,
  image: row.image,
  readingTimeMin: row.reading_time_min,
  status: row.status as BlogStatus,
  scheduledAt: row.scheduled_at,
});
